import React, { useMemo, useRef } from 'react';
import { View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import Text from 'design-system/Text';
import { Button, CardView, rem, Spacer } from 'design-system';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppSelector, useAppStore } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectHasAutoInvest, selectSecclGiaReadyMadeFunds, selectShowISATransfers, selectTradingAccountList, selectWalkthroughAccount, } from 'features/invest/reducers/selectors';
import TotalInvestmentsContainer from 'features/invest/components/TotalInvestmentsContainer';
import SectionHeader from 'design-system/SectionHeader';
import ActiveTradingAccountCard from 'features/invest/components/ActiveTradingAccountCard';
import { useGetBrokerImage } from 'features/invest/utils/getInvestBrokerImage';
import PortfolioHeader from 'features/invest/components/PortfolioHeader';
import { selectCanSellForTradingAccount, usePositions, } from 'features/invest/hooks/selectors';
import Section from 'features/invest/components/Section';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useDefaultHeaderHeight } from 'hooks/useHeaderHeight';
import DiscoverSecclHeaderExtension from 'features/invest/screens/DiscoverSecclHeaderExtension';
import ReadyMadeFund from 'features/invest/components/ReadyMadeFund';
import { AccountTypeDisplayLong } from 'features/invest/constants';
import AutoInvestCard from 'features/invest/components/AutoInvestCard';
import ProductTransferCard from 'features/invest/components/ProductTransferCard';
import { noop } from 'lodash';
import TradingAccountCashRow from 'features/invest/components/TradingAccountCashRow';
import WalkthroughBuySellView from './WalkthroughBuySellView';
import WalkthroughStockPriceView from './WalkthroughStockPriceView';
const emptyPositions = [];
const useInvestWalkthroughItems = (itemPositions, isInTab = true) => {
    const store = useAppStore();
    const headerHeight = useDefaultHeaderHeight();
    const styles = useStyles(styleSet);
    const getBrokerImage = useGetBrokerImage();
    const marginBottomStyles = useMarginBottom('marginBottom');
    const readyMadeFundsAssetIds = useAppSelector(selectSecclGiaReadyMadeFunds);
    const { isAlpacaGIAAccountActive } = useAppSelector(selectTradingAccountList);
    const positions = usePositions('Total value');
    const { paddingHorizontal } = useAppFrameDimensions(isInTab);
    const scrollX = useSharedValue(0);
    const scrollY = useSharedValue(0);
    const secclHorizontalScrollViewRef = useRef(null);
    const hasAutoInvest = useAppSelector(selectHasAutoInvest);
    const showISATransfers = useAppSelector(selectShowISATransfers);
    const marginHorizontalStyle = useMemo(() => ({
        marginHorizontal: paddingHorizontal,
    }), [paddingHorizontal]);
    const walkthroughAccount = useAppSelector(selectWalkthroughAccount);
    const items = useMemo(() => {
        const walkthroughItems = [
            {
                title: 'Total investments',
                description: (<View>
            <Text TextThin-14 Secondary>
              Your total investments reflect the current value of all your
              investment accounts.
            </Text>
            <Spacer h={4}/>
            <Text TextThin-14 Secondary>
              This amount may increase or decrease based on market performance
              and your activity.
            </Text>
          </View>),
                component: (<View style={marginHorizontalStyle} pointerEvents="none">
            <CardView noMargin style={styles.walkthroughCard}>
              <TotalInvestmentsContainer />
            </CardView>
          </View>),
                position: itemPositions?.totalInvestments
                    ? {
                        ...itemPositions.totalInvestments,
                        y: itemPositions.totalInvestments.y - rem(34),
                    }
                    : undefined,
                arrowPosition: 'center',
                bubblePosition: 'bottom',
                screen: 'Invest',
            },
        ];
        if (walkthroughAccount) {
            const { accountType } = walkthroughAccount.account;
            walkthroughItems.push({
                title: 'Investment accounts',
                description: 'This is where you track and manage your investment accounts.',
                component: (<View style={marginHorizontalStyle} pointerEvents="none">
            <CardView noMargin style={styles.walkthroughCard}>
              <SectionHeader style={styles.sectionHeader}>
                Your investment accounts
              </SectionHeader>
              <ActiveTradingAccountCard account={walkthroughAccount} image={isAlpacaGIAAccountActive && accountType === 'GIA'
                        ? getBrokerImage(walkthroughAccount.account.broker)
                        : undefined}/>
              <Spacer h={4}/>
            </CardView>
          </View>),
                position: itemPositions?.investmentAccounts
                    ? {
                        ...itemPositions.investmentAccounts,
                        y: itemPositions.investmentAccounts.y + rem(12),
                    }
                    : undefined,
                screen: 'Invest',
                arrowPosition: 'center',
                bubblePosition: 'bottom',
            });
            walkthroughItems.push({
                title: `${AccountTypeDisplayLong[accountType]} total value`,
                description: `Track how your ${AccountTypeDisplayLong[accountType]} is performing over time. See your balance, returns, and rate of return here.`,
                component: (<View style={marginHorizontalStyle} pointerEvents="none">
            <CardView noMargin style={styles.walkthroughCard}>
              <PortfolioHeader isInTab={!!isInTab}/>
            </CardView>
          </View>),
                position: itemPositions?.portfolioScreenHeader,
                screen: 'InvestPortfolio',
                arrowPosition: 'center',
                bubblePosition: 'bottom',
            });
            const canSell = selectCanSellForTradingAccount(store.getState());
            walkthroughItems.push({
                title: `Fund ${AccountTypeDisplayLong[accountType]}`,
                description: 'Add money anytime to start investing or earning interest.',
                component: (<View style={marginHorizontalStyle} pointerEvents="none">
            <Spacer h={8}/>
            <View style={styles.row}>
              <Spacer w={12}/>
              {!!canSell && <View style={styles.flex}/>}
              {!!canSell && <Spacer w={8}/>}
              <View style={styles.flex}>
                <CardView noMargin style={styles.depositButtonContainer}>
                  <Button small flex brand onPress={noop} title="Invest"/>
                </CardView>
              </View>
              <Spacer w={12}/>
            </View>
            <Spacer h={4}/>
          </View>),
                position: itemPositions?.cashBalanceCard
                    ? {
                        ...itemPositions.cashBalanceCard,
                        y: itemPositions.cashBalanceCard.y,
                    }
                    : undefined,
                screen: 'InvestPortfolio',
                arrowPosition: 'end',
                bubblePosition: 'top',
            });
            if (hasAutoInvest) {
                walkthroughItems.push({
                    title: 'Autoinvest',
                    description: 'Invest in globally diversified ETFs and funds automatically. Stay consistent and grow your investments over time. Capital at risk.',
                    component: (<View style={marginHorizontalStyle} pointerEvents="none">
              <CardView noMargin style={styles.walkthroughCardWithPadding}>
                <AutoInvestCard accountType={accountType}/>
              </CardView>
            </View>),
                    position: itemPositions?.autoinvestCard
                        ? {
                            ...itemPositions.autoinvestCard,
                            y: itemPositions.autoinvestCard.y + rem(4),
                        }
                        : undefined,
                    screen: 'InvestPortfolio',
                    arrowPosition: 'center',
                    bubblePosition: 'top',
                });
            }
            walkthroughItems.push({
                title: 'Your portfolio',
                description: 'This section shows the funds you currently hold. Add another one anytime to grow and diversify your portfolio.',
                component: (<View style={marginHorizontalStyle} pointerEvents="none">
            <CardView noMargin style={styles.walkthroughCardWithPadding}>
              <Section showFilter={false} positions={positions ?? emptyPositions}/>
              <Spacer h={8}/>
            </CardView>
          </View>),
                position: itemPositions?.onYourInvestmentsLayout
                    ? {
                        ...itemPositions.onYourInvestmentsLayout,
                        y: itemPositions.onYourInvestmentsLayout.y + rem(8) - rem(78),
                    }
                    : undefined,
                screen: 'InvestPortfolio',
                overrideScrollYOffset: rem(200),
                arrowPosition: 'center',
                bubblePosition: 'top',
            });
            walkthroughItems.push({
                title: 'Cash balance',
                description: 'Keep track of your uninvested cash here.',
                component: (<View style={marginHorizontalStyle} pointerEvents="none">
            <CardView noMargin style={styles.walkthroughCardWithPadding}>
              <TradingAccountCashRow />
              <Spacer h={8}/>
            </CardView>
          </View>),
                position: itemPositions?.onYourInvestmentsLayout
                    ? {
                        ...itemPositions.onYourInvestmentsLayout,
                        height: rem(78),
                        y: itemPositions.onYourInvestmentsLayout.y +
                            rem(8) +
                            itemPositions.onYourInvestmentsLayout.height -
                            rem(78),
                    }
                    : undefined,
                screen: 'InvestPortfolio',
                arrowPosition: 'center',
                bubblePosition: 'top',
            });
            if (accountType === 'ISA' && !!showISATransfers) {
                walkthroughItems.push({
                    title: 'Transfer an ISA',
                    description: 'Move an existing ISA into your Emma account to keep everything in one place. We’ll handle the transfer for you.',
                    component: (<View style={marginHorizontalStyle} pointerEvents="none">
              <CardView noMargin style={styles.walkthroughCardWithPadding}>
                <ProductTransferCard />
              </CardView>
            </View>),
                    position: itemPositions?.transferAnIsaCard
                        ? {
                            ...itemPositions.transferAnIsaCard,
                            y: itemPositions.transferAnIsaCard.y + rem(4),
                        }
                        : undefined,
                    screen: 'InvestPortfolio',
                    arrowPosition: 'center',
                    bubblePosition: 'top',
                });
            }
            walkthroughItems.push({
                title: 'Let’s find a fund for you',
                description: 'Tap the “search” icon to browse and discover new funds.',
                floatBubbleRight: true,
                component: (<View pointerEvents="none">
            <View style={styles.highLightFloatingSearchButtonContainer}>
              <CardView noMargin style={styles.highLightFloatingSearchButton}/>
            </View>
          </View>),
                position: {
                    x: 0,
                    y: -(marginBottomStyles?.marginBottom || 0) + rem(8),
                    width: FLOATING_SEARCH_BUTTON_SIZE,
                    height: FLOATING_SEARCH_BUTTON_SIZE,
                },
                overrideHeight: (itemPositions?.discoverButton?.y || 0) -
                    (itemPositions?.portfolioScreenHeader?.height || 0),
                screen: 'InvestPortfolio',
                arrowPosition: 'end',
                bubblePosition: 'top',
            });
            walkthroughItems.push({
                title: 'Discover funds',
                description: 'Pick ready-made funds or choose your own. Invest based on your goals and risk level.',
                component: (<View style={marginHorizontalStyle} pointerEvents="none">
            <View style={{ height: headerHeight }}/>
            <CardView noMargin style={styles.walkthroughCard}>
              <DiscoverSecclHeaderExtension scrollX={scrollX} scrollY={scrollY} secclHorizontalScrollViewRef={secclHorizontalScrollViewRef}/>
            </CardView>
          </View>),
                position: itemPositions?.discoverFunds,
                screen: 'InvestDiscover',
                arrowPosition: 'center',
                bubblePosition: 'bottom',
            });
            const readyMadeFundAssetId = readyMadeFundsAssetIds[0];
            if (readyMadeFundAssetId) {
                walkthroughItems.push({
                    title: 'Fund details',
                    description: 'Here’s an example of a ready-made fund. Funds differ by risk and potential return.',
                    component: (<View style={marginHorizontalStyle} pointerEvents="none">
              <View style={{ height: headerHeight + rem(65 + 4) }}/>
              <CardView noMargin style={styles.walkthroughCard}>
                <ReadyMadeFund assetId={readyMadeFundAssetId}/>
              </CardView>
            </View>),
                    position: itemPositions?.discoverFunds,
                    screen: 'InvestDiscover',
                    arrowPosition: 'center',
                    bubblePosition: 'bottom',
                });
                walkthroughItems.push({
                    title: 'Fund price',
                    description: (<View>
              <Text TextThin-14 Secondary>
                See the current price and how it’s changed recently.
              </Text>
              <Spacer h={4}/>
              <Text TextThin-14 Secondary>
                An upward arrow means the value has increased, while a downward
                one means it’s dropped.
              </Text>
            </View>),
                    component: (<View style={marginHorizontalStyle} pointerEvents="none">
              <Spacer h={8}/>
              <CardView style={styles.walkthroughCard} noMargin>
                <WalkthroughStockPriceView assetId={readyMadeFundAssetId}/>
              </CardView>
            </View>),
                    position: itemPositions?.stockScreenStockPrice,
                    screen: 'InvestStock',
                    arrowPosition: 'center',
                    bubblePosition: 'bottom',
                });
                const assetAvailableToSell = store.getState().invest.tradingAccountPositionsCache?.[walkthroughAccount.account.id]?.[readyMadeFundAssetId]?.availableToSell;
                const canSellAsset = assetAvailableToSell
                    ? assetAvailableToSell > 0
                    : false;
                walkthroughItems.push({
                    title: !canSellAsset
                        ? 'Buy your fund or ETF'
                        : 'Manage your investments',
                    description: !canSellAsset
                        ? 'From the fund details screen, you can place a buy order.'
                        : 'From the fund details screen, you can place orders to buy or sell your positions.',
                    component: (<View style={marginHorizontalStyle} pointerEvents="none">
              <WalkthroughBuySellView assetId={readyMadeFundAssetId}/>
            </View>),
                    position: itemPositions?.stockScreenBuy,
                    overrideHeight: rem(80),
                    screen: 'InvestStock',
                    arrowPosition: 'center',
                    bubblePosition: 'top',
                });
            }
        }
        return walkthroughItems;
    }, [
        marginHorizontalStyle,
        styles.walkthroughCard,
        styles.sectionHeader,
        styles.row,
        styles.flex,
        styles.depositButtonContainer,
        styles.walkthroughCardWithPadding,
        styles.highLightFloatingSearchButtonContainer,
        styles.highLightFloatingSearchButton,
        itemPositions?.totalInvestments,
        itemPositions?.investmentAccounts,
        itemPositions?.portfolioScreenHeader,
        itemPositions?.cashBalanceCard,
        itemPositions?.onYourInvestmentsLayout,
        itemPositions?.discoverButton?.y,
        itemPositions?.discoverFunds,
        itemPositions?.autoinvestCard,
        itemPositions?.transferAnIsaCard,
        itemPositions?.stockScreenStockPrice,
        itemPositions?.stockScreenBuy,
        walkthroughAccount,
        isAlpacaGIAAccountActive,
        getBrokerImage,
        isInTab,
        hasAutoInvest,
        positions,
        showISATransfers,
        marginBottomStyles?.marginBottom,
        headerHeight,
        scrollX,
        scrollY,
        readyMadeFundsAssetIds,
    ]);
    return items;
};
const FLOATING_SEARCH_BUTTON_SIZE = rem(106);
const styleSet = createStyleSheets((colors) => ({
    highLightFloatingSearchButtonContainer: {
        height: FLOATING_SEARCH_BUTTON_SIZE - rem(8),
        alignItems: 'flex-end',
        padding: rem(4),
    },
    highLightFloatingSearchButton: {
        width: FLOATING_SEARCH_BUTTON_SIZE - rem(24),
        height: FLOATING_SEARCH_BUTTON_SIZE - rem(24),
        marginRight: rem(8),
        borderRadius: rem(100),
        backgroundColor: colors.cards.onDark,
    },
    walkthroughCard: {
        overflow: 'hidden',
        marginTop: rem(4),
        marginHorizontal: rem(12),
        backgroundColor: colors.background.dark,
    },
    walkthroughCardWithPadding: {
        marginHorizontal: rem(12),
        paddingHorizontal: rem(4),
        backgroundColor: colors.background.dark,
    },
    sectionHeader: {
        paddingHorizontal: rem(4),
        paddingTop: rem(8),
    },
    walkthroughFundThemeCard: {
        width: '100%',
    },
    walkthroughFundThemeCardWrapper: {
        width: '50%',
        marginLeft: rem(4),
        marginTop: rem(4),
    },
    row: {
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    depositButtonContainer: {
        padding: rem(4),
        borderRadius: rem(12),
        backgroundColor: colors.background.dark,
    },
}));
export default useInvestWalkthroughItems;
